/* eslint-disable @hakio/no-untranslated-terminology -- flag names are internal (staff-only panel), not tenant-facing copy */
/**
 * Every feature flag the app reads, with its category, owner and per-environment defaults.
 * Shared by client and server. Overrides set in the internal flag panel live in each environment's
 * own database (`template."FeatureFlagOverride"`); the defaults here are the fallback when there
 * are none, or when they can't be loaded at all.
 *
 * Design: https://linear.app/hakio/document/feature-flags-refinement-notes-and-technical-design-e02e23556f72
 */

export type FlagEnvironment = "production" | "demo" | "test"

/** Which registry defaults apply. Staging, dev, feature branches and local all use `test`. */
export function flagEnvironment(is: { production: boolean; demo: boolean }): FlagEnvironment {
  if (is.production) return "production"
  if (is.demo) return "demo"
  return "test"
}

export const FLAG_CATEGORIES = ["module", "custom", "concierge", "release", "ops"] as const
export type FlagCategory = (typeof FLAG_CATEGORIES)[number]

type FlagDefinitionBase<K extends string> = {
  name: string
  description: string
  owner: string
  /** Linear issue id, e.g. "PRO-123". */
  linear?: string
  /** The flag is only on when every flag listed here is on for the same company/user. */
  requires?: readonly K[]
  defaults: Record<FlagEnvironment, boolean>
}

export type FlagDefinition<K extends string = string> = FlagDefinitionBase<K> &
  (
    | { category: Exclude<FlagCategory, "custom"> }
    | {
        category: "custom"
        /** Why only its companies (listed in `server/customFlagCompanies`). Shipped to every
         * browser: never name a customer. */
        reason: string
        /** ISO date. After it passes, the owner is asked to remove, extend or promote the feature. */
        reviewBy: string
      }
  )

const OFF_EVERYWHERE = { production: false, demo: false, test: false } as const

const defineFlags = <K extends string>(flags: { [P in K]: FlagDefinition<NoInfer<K>> }) => flags

const FLAGS = defineFlags({
  // Modules
  budget: {
    category: "module",
    name: "Budget",
    description: "Budget module",
    owner: "devops@hakio.com",
    defaults: { production: false, demo: true, test: true },
  },
  noosSupplyPlan: {
    category: "module",
    name: "NOOS supply plan",
    description: "NOOS supply plan module",
    owner: "devops@hakio.com",
    defaults: { production: true, demo: true, test: true },
  },
  demandPlan: {
    category: "module",
    name: "Demand plan",
    description: "Demand plan module",
    owner: "devops@hakio.com",
    defaults: { production: false, demo: true, test: true },
  },
  adjustments: {
    category: "module",
    name: "Adjustments",
    description: "Forecast adjustments",
    owner: "martin@hakio.com",
    defaults: { production: true, demo: true, test: true },
  },
  reporting: {
    category: "module",
    name: "Reporting",
    description: "Reporting page",
    owner: "martin@hakio.com",
    defaults: OFF_EVERYWHERE,
  },
  customerInsightsPage: {
    category: "module",
    name: "Customer insights",
    description: "Customer insights page",
    owner: "martin@hakio.com",
    defaults: { production: true, demo: true, test: true },
  },
  orderSuggestionsPage: {
    category: "module",
    name: "Order suggestions",
    description: "Order suggestions page",
    owner: "martin@hakio.com",
    defaults: { production: true, demo: true, test: true },
  },

  // Custom
  reviewForecasts: {
    category: "custom",
    reason: "Forecast review flow built as a proof of concept for one customer",
    reviewBy: "2026-12-31",
    name: "Review forecasts",
    description: "Review flow for forecasts",
    owner: "devops@hakio.com",
    defaults: OFF_EVERYWHERE,
  },
  noosVendorCurrency: {
    category: "custom",
    reason: "Vendor currency properties on the NOOS buy plan, built for one customer",
    reviewBy: "2026-12-31",
    requires: ["noosSupplyPlan"],
    name: "NOOS vendor currency",
    description: "Set vendor currency properties on the NOOS buy plan",
    owner: "devops@hakio.com",
    defaults: OFF_EVERYWHERE,
  },

  // Concierge
  forecastEvaluation: {
    category: "concierge",
    name: "Forecast evaluation",
    description: "Forecast evaluation",
    owner: "martin@hakio.com",
    defaults: { production: true, demo: true, test: true },
  },
  budgetEstimatedSalesPrice: {
    category: "concierge",
    requires: ["budget"],
    name: "Budget estimated sales price",
    description: "Work with estimatedSalesPrice in Budget",
    owner: "devops@hakio.com",
    defaults: { production: false, demo: true, test: true },
  },
  columnRelations: {
    category: "concierge",
    name: "Column relations",
    description: "Column relations",
    owner: "devops@hakio.com",
    defaults: { production: false, demo: true, test: true },
  },
  demandPlanningV2: {
    category: "concierge",
    name: "Demand planning V2",
    description: "Demand planning V2",
    owner: "martin@hakio.com",
    defaults: { production: false, demo: true, test: true },
  },
  demandPlanSeasonality: {
    category: "concierge",
    requires: ["demandPlan"],
    name: "Demand plan seasonality",
    description: "Seasonality tab in the demand plan",
    owner: "devops@hakio.com",
    defaults: OFF_EVERYWHERE,
  },
  demandPlanSplits: {
    category: "concierge",
    requires: ["demandPlan"],
    name: "Demand plan splits",
    description: "Splits tab in the demand plan",
    owner: "devops@hakio.com",
    defaults: OFF_EVERYWHERE,
  },
  demandPlanTrim: {
    category: "concierge",
    requires: ["demandPlan"],
    name: "Demand plan trim",
    description: "Trim tab in the demand plan",
    owner: "devops@hakio.com",
    defaults: OFF_EVERYWHERE,
  },

  // Release
  unfulfillablePreorders: {
    category: "release",
    name: "Unfulfillable preorders",
    description: "Unfulfillable preorders columns",
    owner: "devops@hakio.com",
    defaults: { production: false, demo: false, test: true },
  },
  columnChooserV2: {
    category: "release",
    linear: "HAK-3090",
    name: "Column chooser v2",
    description: "Column chooser redesign",
    owner: "devops@hakio.com",
    defaults: { production: false, demo: false, test: true },
  },
  budgetUnreleased: {
    category: "release",
    linear: "BUD-228",
    requires: ["budget"],
    name: "Budget unreleased features",
    description:
      "Budget features that aren't production ready yet, such as the markdown impact link",
    owner: "devops@hakio.com",
    defaults: OFF_EVERYWHERE,
  },
  budgetVariantLevel: {
    category: "release",
    requires: ["budget"],
    name: "Budget variant level",
    description: "Variant-level table in Budget",
    owner: "devops@hakio.com",
    defaults: { production: true, demo: true, test: true },
  },
  demandPlanOnboarding: {
    category: "release",
    requires: ["demandPlan"],
    name: "Demand plan onboarding",
    description: "Onboarding flow for the demand plan",
    owner: "devops@hakio.com",
    defaults: { production: true, demo: false, test: true },
  },

  // Ops
  ingestionCockpit: {
    category: "ops",
    name: "Ingestion cockpit",
    description: "Staff-only ingestion cockpit in the internal sidebar. Off hides it.",
    owner: "devops@hakio.com",
    defaults: { production: true, demo: true, test: true },
  },
})

export type FeatureFlagKey = keyof typeof FLAGS

export const FEATURE_FLAGS: Readonly<Record<FeatureFlagKey, FlagDefinition<FeatureFlagKey>>> = FLAGS

/** Keys that were removed. A stored override outlives its flag until someone deletes the row, so a
 * reused key would inherit it. `registry.test.ts` rejects a live key listed here. */
export const RETIRED_FLAG_KEYS: readonly string[] = ["enforceGates"]

/** LaunchDarkly flag that switches the app to the in-house flag system. Off = LaunchDarkly. */
export const HAKIO_FLAGS_SWITCH = "hakioFeatureFlags"

export const FEATURE_FLAG_KEYS = Object.keys(FEATURE_FLAGS) as FeatureFlagKey[]

export const isFeatureFlagKey = (key: string): key is FeatureFlagKey =>
  Object.hasOwn(FEATURE_FLAGS, key)

/** The flag's name, else its key (a flag no longer in the registry). */
export const flagName = (key: string) => (isFeatureFlagKey(key) ? FEATURE_FLAGS[key].name : key)

/** Flag keys ordered Module → Custom → Concierge → Release → Ops, by name within a category. */
export const FLAG_KEYS_BY_CATEGORY: FeatureFlagKey[] = FEATURE_FLAG_KEYS.toSorted((a, b) => {
  const byCategory =
    FLAG_CATEGORIES.indexOf(FEATURE_FLAGS[a].category) -
    FLAG_CATEGORIES.indexOf(FEATURE_FLAGS[b].category)
  return byCategory || FEATURE_FLAGS[a].name.localeCompare(FEATURE_FLAGS[b].name)
})
